import type { LiveChatMessage } from "@hobbyshopify/backend-client";
import { type ReactNode, useEffect, useRef } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { CockpitChatActions, CockpitChatState } from "./cockpit-chat-controller";

const OVERLAY_MESSAGES = 5;

/** One colour per person, so a busy room reads as people rather than a wall of names. */
const NAME_COLOURS = ["#2fe3b4", "#f2a65a", "#c08bf0", "#6fb7ff", "#f27d9b", "#e3d35b"];

function nameColour(userId: string): string {
  let h = 0;
  for (const c of userId) h = (h * 31 + c.charCodeAt(0)) | 0;
  return NAME_COLOURS[Math.abs(h) % NAME_COLOURS.length] as string;
}

/**
 * The room, in the room where the host is. Reading chat is how you know to slow
 * down, re-show a card, or answer "is spot 9 still open".
 *
 * A column of its own on a wide screen; on anything narrower the last few lines
 * float over the picture, the way the room itself shows them. Before the stream
 * it is not open yet, and after it the room is read-only.
 */
export function CockpitChat({
  overlay = false,
  closed = null,
  summary = null,
}: {
  overlay?: boolean;
  closed?: "prelive" | "ended" | null;
  /** The ended stream's one line: how long, how many boxes. */
  summary?: string | null;
}) {
  const state = useSubtree(CockpitChatState);
  const actions = useSubtree(CockpitChatActions);
  const observer = useObserver();
  const all = observer.watch(state.messages);
  const draft = observer.watch(state.draft);
  const post = observer.watch(state.post);
  const hide = observer.watch(state.hide);
  const bottom = useRef<HTMLDivElement>(null);
  const messages = overlay ? all.slice(-OVERLAY_MESSAGES) : all;

  // Newest message in view. Only when the list itself changes, so a host who has
  // scrolled back to read something is not fought for control on every render.
  useEffect(() => {
    if (overlay || all.length === 0) return;
    bottom.current?.scrollIntoView({ block: "end" });
  }, [overlay, all.length]);

  const failure = post.error ?? hide.error;

  if (overlay) {
    return (
      <div className="flex flex-col gap-2">
        <ul className="flex flex-col gap-1 text-[13px] [text-shadow:0_1px_2px_rgb(0_0_0/0.9)] [mask-image:linear-gradient(to_bottom,transparent,black_40%)]">
          {messages.map((m) => (
            <Line key={m.id} message={m} />
          ))}
        </ul>
        <Composer draft={draft} busy={post.busy} compact onDraft={actions.setDraft} onSend={actions.send} />
        {failure && <p className="note note-danger">{failure}</p>}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {closed === "prelive" ? (
        <p className="flex flex-1 items-center justify-center p-4 text-center text-[13px] text-white/40">
          Chat opens when you go live.
        </p>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {messages.length === 0 && !summary ? (
            <p className="py-6 text-center text-sm text-white/40">No messages yet.</p>
          ) : (
            <ul className="flex min-h-full flex-col justify-end gap-2.5 text-[13px]">
              {messages.map((m) => (
                <Line
                  key={m.id}
                  message={m}
                  onHide={closed ? undefined : () => actions.hide(m.id)}
                  hiding={hide.busy}
                />
              ))}
              {summary && <SystemLine tone="neutral">{summary}</SystemLine>}
              <div ref={bottom} />
            </ul>
          )}
        </div>
      )}

      {closed === "ended" ? (
        <p className="border-t border-[color:var(--color-line)] px-4 py-4 text-center text-[13px] text-white/40">
          Chat is closed.
        </p>
      ) : (
        <div className="border-t border-[color:var(--color-line)] px-4 pt-3 pb-4">
          <Composer
            draft={draft}
            busy={post.busy}
            disabled={closed === "prelive"}
            onDraft={actions.setDraft}
            onSend={actions.send}
          />
          {failure && <p className="note note-danger mt-2">{failure}</p>}
        </div>
      )}
    </div>
  );
}

function Line({ message, onHide, hiding }: { message: LiveChatMessage; onHide?: () => void; hiding?: boolean }) {
  if (message.kind !== "USER") {
    return (
      <SystemLine
        tone={
          message.kind === "RIP_STARTED"
            ? "danger"
            : message.kind === "PURCHASE" || message.kind === "DRAW"
              ? "accent"
              : "neutral"
        }
      >
        {(message.kind === "PURCHASE" || message.kind === "DRAW") && (
          <span className="font-bold text-white">{message.authorName} </span>
        )}
        {message.text}
      </SystemLine>
    );
  }

  return (
    <li className="group flex items-start gap-2">
      <span className="min-w-0 flex-1 break-words">
        <span className="font-bold" style={{ color: nameColour(message.userId) }}>
          {message.authorName}
        </span>{" "}
        {message.text}
      </span>
      {onHide && (
        <button
          type="button"
          className="btn-ghost btn-xs shrink-0 opacity-0 transition group-hover:opacity-60 focus:opacity-60"
          disabled={hiding}
          onClick={onHide}
          aria-label={`Hide message from ${message.authorName}`}
        >
          Hide
        </button>
      )}
    </li>
  );
}

function SystemLine({ tone, children }: { tone: "accent" | "danger" | "neutral"; children: ReactNode }) {
  const edge =
    tone === "danger"
      ? "border-[color:var(--color-danger)]"
      : tone === "accent"
        ? "border-[color:var(--color-accent)]"
        : "border-white/20";
  return (
    <li className={`rounded-r-md border-l-2 bg-white/[0.04] px-2.5 py-1.5 text-xs text-white/55 ${edge}`}>
      {children}
    </li>
  );
}

function Composer({
  draft,
  busy,
  disabled = false,
  compact = false,
  onDraft,
  onSend,
}: {
  draft: string;
  busy: boolean;
  disabled?: boolean;
  compact?: boolean;
  onDraft: (text: string) => void;
  onSend: () => void;
}) {
  return (
    <div className="flex gap-2">
      <input
        className={`h-10 min-w-0 flex-1 rounded-lg border border-white/10 px-3 text-sm text-white placeholder:text-white/35 disabled:opacity-50 ${
          compact ? "bg-black/50 backdrop-blur" : "bg-white/[0.04]"
        }`}
        aria-label="Say something"
        placeholder="Say something"
        value={draft}
        maxLength={500}
        disabled={disabled}
        onChange={(event) => onDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          onSend();
        }}
      />
      {(!compact || draft.trim() !== "") && (
        <button
          type="button"
          className="h-10 rounded-lg bg-white/[0.07] px-3.5 text-sm font-bold text-[color:var(--color-accent)] disabled:opacity-40"
          disabled={disabled || busy || draft.trim() === ""}
          onClick={onSend}
        >
          Send
        </button>
      )}
    </div>
  );
}
